import { useQuery } from "@tanstack/react-query";
import { cn } from "@tcg/ui";
import {
  Camera,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  UserPlus,
  UserRound,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { call } from "@/lib/api";
import type { BuylistCreate } from "@/lib/types";

export type IdentityStatus = "missing" | "valid" | "expired";

export interface SellerChoice {
  body: BuylistCreate;
  label: string;
}

/** Only the first character + ＊ — the history of the shift never shows a full name (APPI). */
export function maskName(name: string | null | undefined): string {
  if (!name) {
    return "—";
  }
  const chars = [...name.trim()];
  return chars.length <= 1
    ? "＊"
    : `${chars[0] ?? ""}${"＊".repeat(Math.min(3, chars.length - 1))}`;
}

/**
 * The seller (T07-31): a known customer (search by name / e-mail — store credit needs one), a new
 * customer with an e-mail, or a walk-in seller (name + phone — cash / bank transfer only).
 */
export function SellerPicker({
  onChoose,
  busy,
}: {
  onChoose: (choice: SellerChoice) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const { api } = usePos();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState<"search" | "new">("search");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const term = q.trim();
  const found = useQuery({
    queryKey: ["pos-customers", "list", "buylist", term],
    queryFn: () =>
      call(() => api.GET("/v1/pos/customers", { params: { query: { q: term, limit: 10 } } })),
    enabled: term.length >= 3,
    staleTime: 10_000,
  });

  const createNew = async () => {
    if (!name.trim()) {
      return;
    }
    if (!email.trim()) {
      onChoose({
        body: { quick: { name: name.trim(), ...(phone.trim() ? { phone: phone.trim() } : {}) } },
        label: name.trim(),
      });
      return;
    }
    setCreating(true);
    try {
      const customer = await call(() =>
        api.POST("/v1/pos/customers", {
          body: {
            name: name.trim(),
            email: email.trim(),
            ...(phone.trim() ? { phone: phone.trim() } : {}),
          },
        }),
      );
      onChoose({ body: { customerId: customer.id }, label: customer.name ?? name.trim() });
    } finally {
      setCreating(false);
    }
  };

  return (
    <section aria-labelledby="seller-title" className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 id="seller-title" className="text-xl font-semibold">
        {t("buylist.seller_title")}
      </h2>
      <div className="flex gap-2">
        <button
          type="button"
          aria-pressed={mode === "search"}
          onClick={() => setMode("search")}
          className={cn(
            "flex min-h-pos-touch items-center gap-2 rounded-md border px-4 text-lg",
            mode === "search" && "border-primary bg-primary text-primary-foreground",
          )}
        >
          <UserRound aria-hidden className="size-5" />
          {t("buylist.seller_search")}
        </button>
        <button
          type="button"
          aria-pressed={mode === "new"}
          onClick={() => setMode("new")}
          className={cn(
            "flex min-h-pos-touch items-center gap-2 rounded-md border px-4 text-lg",
            mode === "new" && "border-primary bg-primary text-primary-foreground",
          )}
        >
          <UserPlus aria-hidden className="size-5" />
          {t("buylist.seller_new")}
        </button>
      </div>
      {mode === "search" ? (
        <>
          <label className="flex flex-col gap-1">
            <span className="sr-only">{t("buylist.seller_search")}</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("customer.search_placeholder")}
              autoComplete="off"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <ul className="flex flex-col gap-1">
            {(found.data?.data ?? []).map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    onChoose({ body: { customerId: c.id }, label: c.name ?? c.email ?? "—" })
                  }
                  className="flex min-h-pos-touch w-full items-center gap-2 rounded-md border px-3 text-left text-lg"
                >
                  <UserRound aria-hidden className="size-5" />
                  {c.name ?? c.email}
                  {c.email ? (
                    <span className="text-sm text-muted-foreground">{c.email}</span>
                  ) : null}
                </button>
              </li>
            ))}
            {term.length >= 3 && found.data && found.data.data.length === 0 ? (
              <li className="text-muted-foreground">{t("customer.none_found")}</li>
            ) : null}
          </ul>
        </>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("customer.name")}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("customer.phone")}</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoComplete="off"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("customer.email")}</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoComplete="off"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <p className="text-sm text-muted-foreground md:col-span-3">
            {t("buylist.seller_new_note")}
          </p>
          <button
            type="button"
            onClick={() => void createNew()}
            disabled={!name.trim() || busy || creating}
            className="min-h-pos-touch rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50 md:col-span-3 md:justify-self-start"
          >
            {t("buylist.start")}
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * The 本人確認 bar (T07-31): 未確認 / 確認済（期限）/ 期限切れ + 「📷 撮影して確認」. The shop checks
 * every seller (the law from ¥10,000 — `legalMandatory`), so the bar is always there.
 */
export function IdentityBar({
  seller,
  status,
  expiresAt,
  legalMandatory,
  onCheck,
  disabled,
}: {
  seller: string;
  status: IdentityStatus;
  expiresAt: string | null;
  legalMandatory: boolean;
  onCheck: () => void;
  disabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const date = expiresAt
    ? new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "ja", {
        dateStyle: "medium",
        timeZone: "Asia/Tokyo",
      }).format(new Date(expiresAt))
    : null;
  const Icon =
    status === "valid" ? ShieldCheck : status === "expired" ? ShieldAlert : ShieldQuestion;
  return (
    <section
      aria-label={t("buylist.identity")}
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-lg border-2 p-3",
        status === "valid" ? "border-pos-ok" : "border-status-problem",
      )}
    >
      <UserRound aria-hidden className="size-6" />
      <strong className="text-lg">{seller}</strong>
      <span
        role="status"
        className={cn(
          "flex items-center gap-1 rounded-full px-3 py-1 font-semibold",
          status === "valid" ? "bg-pos-ok text-white" : "bg-status-problem text-white",
        )}
      >
        <Icon aria-hidden className="size-5" />
        {status === "valid"
          ? date
            ? t("buylist.identity_valid_until", { date })
            : t("buylist.identity_valid")
          : status === "expired"
            ? t("buylist.identity_expired")
            : t("buylist.identity_missing")}
      </span>
      <span className="text-sm text-muted-foreground">
        {legalMandatory ? t("buylist.identity_law") : t("buylist.identity_shop_rule")}
      </span>
      {status !== "valid" ? (
        <button
          type="button"
          onClick={onCheck}
          disabled={disabled}
          className="ml-auto flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-5 text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          <Camera aria-hidden className="size-6" />
          {t("buylist.identity_check")}
        </button>
      ) : null}
    </section>
  );
}
